/**
 * IT Access Authorization panel.
 *
 * Rendered inside the HR and Legal & Compliance department landings.
 * Each officer can independently approve or revoke their half of the
 * dual-approval. An IT agent only becomes `it_authorized = true` when
 * BOTH HR and Legal have approved.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ShieldCheck, ShieldOff, Cpu, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ITAuthorizationsPanel({ officerRole /* 'hr' | 'legal' */ }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/it-authorizations`, { withCredentials: true });
      setUsers(res.data.users || []);
      setAccessDenied(false);
    } catch (err) {
      // A background-only panel must not present a forbidden department lookup
      // as an error in the unrelated employee form. Explicit actions still report errors.
      if (err.response?.status === 403) setAccessDenied(true);
      else toast.error(err.response?.data?.detail || 'Could not load IT authorizations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function act(userId, action) {
    setActing(userId);
    try {
      await axios.post(`${API}/it-authorizations/${userId}/${action}`, {}, { withCredentials: true });
      toast.success(action === 'approve' ? 'Approval recorded.' : 'Approval revoked.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not ${action}.`);
    } finally {
      setActing(null);
    }
  }

  const myField = officerRole === 'hr' ? 'it_authorized_by_hr' : 'it_authorized_by_legal';
  const otherField = officerRole === 'hr' ? 'it_authorized_by_legal' : 'it_authorized_by_hr';
  const otherLabel = officerRole === 'hr' ? 'Legal & Compliance' : 'Human Resources';

  if (accessDenied) return null;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 mt-4" data-testid={`it-auth-panel-${officerRole}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'rgba(201,169,98,0.18)', border: '1px solid rgba(201,169,98,0.4)' }}
          >
            <ShieldCheck className="h-5 w-5" style={{ color: '#C9A962' }} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest" style={{ color: '#C9A962', letterSpacing: '0.2em' }}>
              IT Access Authorization
            </p>
            <p className="text-sm text-white font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {officerRole === 'hr' ? 'Human Resources approval' : 'Legal & Compliance approval'}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="it-auth-reload">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <p className="text-xs text-zinc-400 mb-4">
        Dual-approval is required to grant an IT agent elevated visibility into Executive Office and
        Human Resources data. Both <span className="text-white">Human Resources</span> and{' '}
        <span className="text-white">Legal &amp; Compliance</span> must approve before access is granted.
      </p>

      {loading && <p className="text-xs text-zinc-500">Loading IT agents…</p>}
      {!loading && users.length === 0 && (
        <p className="text-xs text-zinc-500">No IT agents found.</p>
      )}

      {users.length > 0 && (
        <div className="space-y-2" data-testid="it-auth-list">
          {users.map((u) => {
            const mine = !!u[myField];
            const other = !!u[otherField];
            const granted = !!u.it_authorized;
            return (
              <div
                key={u.id}
                className="flex flex-wrap items-center gap-3 rounded-lg p-3"
                style={{ backgroundColor: 'rgba(20,20,20,0.7)', border: '1px solid rgba(201,169,98,0.18)' }}
                data-testid={`it-auth-row-${u.id}`}
              >
                <Cpu className="h-4 w-4" style={{ color: '#C9A962' }} />
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm text-white font-medium">{u.name}</p>
                  <p className="text-[11px] text-zinc-500 font-mono">{u.badge_number}</p>
                </div>
                <Badge
                  className="text-[10px]"
                  style={{
                    backgroundColor: mine ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.06)',
                    color: mine ? '#86efac' : '#a1a1aa',
                    border: `1px solid ${mine ? 'rgba(34,197,94,0.45)' : 'rgba(255,255,255,0.12)'}`,
                  }}
                >
                  {officerRole === 'hr' ? 'HR' : 'LEG'}: {mine ? 'approved' : 'pending'}
                </Badge>
                <Badge
                  className="text-[10px]"
                  style={{
                    backgroundColor: other ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.06)',
                    color: other ? '#86efac' : '#a1a1aa',
                    border: `1px solid ${other ? 'rgba(34,197,94,0.45)' : 'rgba(255,255,255,0.12)'}`,
                  }}
                >
                  {otherLabel.split(' ')[0]}: {other ? 'approved' : 'pending'}
                </Badge>
                <Badge
                  className="text-[10px]"
                  style={{
                    backgroundColor: granted ? 'rgba(201,169,98,0.2)' : 'rgba(255,255,255,0.06)',
                    color: granted ? '#C9A962' : '#a1a1aa',
                    border: `1px solid ${granted ? '#C9A962' : 'rgba(255,255,255,0.12)'}`,
                  }}
                >
                  {granted ? 'ACCESS GRANTED' : 'awaiting both'}
                </Badge>
                {mine ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={acting === u.id}
                    onClick={() => act(u.id, 'revoke')}
                    data-testid={`it-auth-revoke-${u.id}`}
                  >
                    <ShieldOff className="h-3.5 w-3.5 mr-1" /> Revoke
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={acting === u.id}
                    onClick={() => act(u.id, 'approve')}
                    style={{ backgroundColor: '#C9A962', color: '#000' }}
                    data-testid={`it-auth-approve-${u.id}`}
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
